import type { FieldsetHTMLAttributes, ReactNode } from 'react';
import { clsx } from '../clsx';
import { RequiredMark } from './RequiredMark';
import styles from './FieldGroup.module.css';

export interface FieldGroupProps extends FieldsetHTMLAttributes<HTMLFieldSetElement> {
  /** Group label, e.g. "Nächster Schritt" above radio cards or "Neuer Status". */
  legend?: string | undefined;
  required?: boolean | undefined;
  description?: string | undefined;
  error?: string | undefined;
  /** Children stacked (`1`) or laid out as a grid of equal columns (radio cards: 3). */
  columns?: 1 | 2 | 3 | 4 | undefined;
  /** Gap between the children: 8 (option cards, checkboxes) or 14 (fields). */
  gap?: 8 | 14 | undefined;
  children: ReactNode;
}

/** Fieldset with the 12 px label of the screens; groups radio cards, checkboxes or related fields. */
export function FieldGroup({
  legend,
  required,
  description,
  error,
  columns = 1,
  gap = 8,
  className,
  children,
  ...rest
}: FieldGroupProps) {
  return (
    <fieldset className={clsx(styles.group, className)} aria-invalid={error ? true : undefined} {...rest}>
      {legend ? (
        <legend className={styles.legend}>
          {legend}
          {required ? <RequiredMark /> : null}
        </legend>
      ) : null}
      {description ? <div className={styles.description}>{description}</div> : null}
      <div
        className={clsx(styles.items, gap === 14 && styles.gap14)}
        style={
          columns > 1
            ? { display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
            : undefined
        }
      >
        {children}
      </div>
      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : null}
    </fieldset>
  );
}
